import { useEffect, useMemo, useSyncExternalStore } from 'react';
import { bindVoiceLifecycle, VoiceController, type VoiceControllerDeps, type VoiceSnapshot } from './controller';

export interface VoiceCapture {
  state: VoiceSnapshot;
  controller: VoiceController;
}

/**
 * React binding for the VoiceController. The controller instance lives for the component's lifetime;
 * configuration and callbacks are read through refs-like getters so the hook never restarts a capture.
 */
export function useVoiceCapture(deps: VoiceControllerDeps, onSignOut: (cb: () => void) => () => void): VoiceCapture {
  const latest = useMemo(() => ({ deps }), []);
  latest.deps = deps;
  const controller = useMemo(
    () =>
      new VoiceController({
        ...deps,
        api: deps.api,
        getConfig: () => latest.deps.getConfig(),
        onCommand: (cmd) => latest.deps.onCommand(cmd),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  useEffect(() => {
    const unbind = bindVoiceLifecycle(controller, { onSignOut });
    return () => {
      unbind();
      controller.dispose();
    };
  }, [controller, onSignOut]);
  useEffect(() => {
    controller.refreshMode();
  });
  const state = useSyncExternalStore(controller.subscribe, controller.getSnapshot, controller.getSnapshot);
  return { state, controller };
}
